<script setup>
import { RouterLink } from 'vue-router'
import { ROUTES } from '../../../../core/config/constants.js'
import BusinessBanner from './BusinessBanner.vue'

defineEmits(['scroll-to-stats'])
</script>

<template>
  <!-- Desktop hero content (hidden on mobile, shown on desktop) -->
  <div class="hero-content hero-content-desktop">
    <div class="hero-badge-wrapper">
      <div class="hero-badge">
        <span class="badge-dot"></span>
        <span>מובילים בייבוא מזון בישראל</span>
      </div>
      <div class="hero-sub-badge">
        <span class="sub-badge-dot"></span>
        <span>ועכשיו גם פארם</span>
      </div>
    </div>
    <h1 class="hero-title">
      <span class="title-line">הבחירה של</span>
      <span class="title-highlight">הקמעונאים</span>
    </h1>
    <p class="hero-subtitle">
      חברת יבוא ושיווק מזון מובילה, עם אלפי לקוחות בכל רחבי הארץ. מותגים מובילים, מחירים תחרותיים,
      ומשלוחים לכל נקודה בישראל.
    </p>
    <div class="hero-actions">
      <RouterLink :to="{ name: ROUTES.CATALOG }" class="btn-primary-lg">
        <span>הקטלוג שלנו</span>
      </RouterLink>
      <button @click="$emit('scroll-to-stats')" class="btn-secondary-lg">
        <span>עוד עלינו</span>
      </button>
    </div>
  </div>
  <div class="hero-visual">
    <!-- Mobile Phone with Shopping Elements -->
    <div class="phone-container">
      <svg class="phone-svg" viewBox="0 0 280 560" fill="none" xmlns="http://www.w3.org/2000/svg">
        <!-- Phone Frame -->
        <defs>
          <linearGradient id="phoneFrame" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color: #1e293b" />
            <stop offset="50%" style="stop-color: #334155" />
            <stop offset="100%" style="stop-color: #1e293b" />
          </linearGradient>
          <linearGradient id="screenGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color: #f8fafc" />
            <stop offset="100%" style="stop-color: #e2e8f0" />
          </linearGradient>
          <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color: #f59e0b" />
            <stop offset="100%" style="stop-color: #d97706" />
          </linearGradient>
          <linearGradient id="goldGradientLight" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color: #fbbf24" />
            <stop offset="100%" style="stop-color: #f59e0b" />
          </linearGradient>
          <filter id="cartShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow
              dx="0"
              dy="4"
              stdDeviation="8"
              flood-color="#F59E0B"
              flood-opacity="0.3"
            />
          </filter>
          <filter id="phoneShadow" x="-20%" y="-10%" width="140%" height="130%">
            <feDropShadow
              dx="0"
              dy="20"
              stdDeviation="30"
              flood-color="#1e293b"
              flood-opacity="0.4"
            />
          </filter>
          <clipPath id="screenClip">
            <rect x="16" y="16" width="248" height="528" rx="28" />
          </clipPath>
        </defs>

        <!-- Phone Body with Shadow -->
        <g filter="url(#phoneShadow)">
          <rect x="0" y="0" width="280" height="560" rx="44" fill="url(#phoneFrame)" />
          <rect
            x="2"
            y="2"
            width="276"
            height="556"
            rx="42"
            fill="none"
            stroke="rgba(255,255,255,0.1)"
            stroke-width="1"
          />
        </g>

        <!-- Screen -->
        <rect x="12" y="12" width="256" height="536" rx="32" fill="url(#screenGradient)" />

        <!-- Screen Content (clipped) -->
        <g clip-path="url(#screenClip)">
          <!-- Status Bar -->
          <rect x="16" y="16" width="248" height="44" fill="#1e293b" />
          <text x="140" y="44" text-anchor="middle" fill="white" font-size="14" font-weight="600">
            GoFood
          </text>

          <!-- Notch -->
          <rect x="95" y="20" width="90" height="28" rx="14" fill="#0f172a" />
          <circle cx="140" cy="34" r="6" fill="#1e293b" />

          <!-- App Header -->
          <rect x="16" y="60" width="248" height="60" fill="#1e293b" />

          <!-- Search Bar -->
          <rect
            x="28"
            y="132"
            width="224"
            height="44"
            rx="12"
            fill="white"
            stroke="#e2e8f0"
            stroke-width="1"
          />
          <circle cx="224" cy="154" r="10" fill="none" stroke="#94a3b8" stroke-width="2" />
          <line
            x1="217"
            y1="161"
            x2="212"
            y2="166"
            stroke="#94a3b8"
            stroke-width="2"
            stroke-linecap="round"
          />

          <!-- Card 1 - SALE badge -->
          <rect
            x="28"
            y="192"
            width="104"
            height="130"
            rx="12"
            fill="white"
            stroke="#e2e8f0"
            stroke-width="1"
          />
          <rect x="38" y="202" width="84" height="60" rx="8" fill="#fef3c7" />
          <circle cx="80" cy="232" r="20" fill="url(#goldGradientLight)" opacity="0.6" />
          <text x="80" y="288" text-anchor="middle" fill="#334155" font-size="11" font-weight="600">
            שוקולד
          </text>
          <rect x="38" y="298" width="84" height="20" rx="10" fill="#EF4444" />
          <text x="80" y="312" text-anchor="middle" fill="white" font-size="10" font-weight="700">
            SALE
          </text>

          <!-- Card 2 - 50% badge -->
          <rect
            x="148"
            y="192"
            width="104"
            height="130"
            rx="12"
            fill="white"
            stroke="#e2e8f0"
            stroke-width="1"
          />
          <rect x="158" y="202" width="84" height="60" rx="8" fill="#dbeafe" />
          <circle cx="200" cy="232" r="20" fill="#60a5fa" opacity="0.5" />
          <text
            x="200"
            y="288"
            text-anchor="middle"
            fill="#334155"
            font-size="11"
            font-weight="600"
          >
            חטיפים
          </text>
          <rect x="158" y="298" width="84" height="20" rx="10" fill="url(#goldGradient)" />
          <text x="200" y="312" text-anchor="middle" fill="white" font-size="10" font-weight="700">
            50%
          </text>

          <!-- Card 3 - Discount badge -->
          <rect
            x="28"
            y="338"
            width="104"
            height="130"
            rx="12"
            fill="white"
            stroke="#e2e8f0"
            stroke-width="1"
          />
          <rect x="38" y="348" width="84" height="60" rx="8" fill="#dcfce7" />
          <circle cx="80" cy="378" r="20" fill="#4ade80" opacity="0.5" />
          <text x="80" y="434" text-anchor="middle" fill="#334155" font-size="11" font-weight="600">
            משקאות
          </text>
          <rect x="38" y="444" width="84" height="20" rx="10" fill="#8B5CF6" />
          <text x="80" y="458" text-anchor="middle" fill="white" font-size="9" font-weight="700">
            DISCOUNT
          </text>

          <!-- Card 4 - חיסול badge -->
          <rect
            x="148"
            y="338"
            width="104"
            height="130"
            rx="12"
            fill="white"
            stroke="#e2e8f0"
            stroke-width="1"
          />
          <rect x="158" y="348" width="84" height="60" rx="8" fill="#fce7f3" />
          <circle cx="200" cy="378" r="20" fill="#f472b6" opacity="0.5" />
          <text
            x="200"
            y="434"
            text-anchor="middle"
            fill="#334155"
            font-size="11"
            font-weight="600"
          >
            ממתקים
          </text>
          <rect x="158" y="444" width="84" height="20" rx="10" fill="#1e293b" />
          <text
            x="200"
            y="458"
            text-anchor="middle"
            fill="#FBBF24"
            font-size="10"
            font-weight="700"
          >
            חיסול
          </text>

          <!-- Bottom Navigation -->
          <rect x="16" y="484" width="248" height="60" fill="white" />
          <line x1="16" y1="484" x2="264" y2="484" stroke="#e2e8f0" stroke-width="1" />

          <!-- Nav Icons -->
          <g fill="none" stroke="#94a3b8" stroke-width="2">
            <path
              d="M56 504 L56 520 L72 520 L72 504 L64 496 L56 504"
              transform="translate(-8, 0)"
            />
            <circle cx="110" cy="510" r="8" />
            <line x1="116" y1="516" x2="120" y2="520" />
          </g>
          <g fill="none" stroke="url(#goldGradient)" stroke-width="2.5">
            <circle cx="168" cy="518" r="3" fill="#F59E0B" />
            <circle cx="182" cy="518" r="3" fill="#F59E0B" />
            <path d="M158 500 L162 500 L166 514 L186 514 L190 504 L168 504" />
          </g>
          <g fill="none" stroke="#94a3b8" stroke-width="2">
            <circle cx="230" cy="506" r="6" />
            <path d="M220 522 C220 516 224 512 230 512 C236 512 240 516 240 522" />
          </g>

          <!-- Cart Badge -->
          <circle cx="188" cy="498" r="10" fill="url(#goldGradient)" />
          <text x="188" y="502" text-anchor="middle" fill="white" font-size="11" font-weight="700">
            3
          </text>
        </g>

        <!-- Home Indicator -->
        <rect x="100" y="538" width="80" height="5" rx="2.5" fill="#334155" />
      </svg>

      <!-- Floating Shopping Carts -->
      <div class="floating-cart floating-cart-1">
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="24" cy="24" r="24" fill="white" />
          <circle cx="24" cy="24" r="22" fill="url(#cartBg1)" />
          <g transform="translate(12, 10)">
            <circle cx="8" cy="26" r="3" fill="#D97706" />
            <circle cx="20" cy="26" r="3" fill="#D97706" />
            <path
              d="M0 2 H4 L8 18 H22 L26 8 H10"
              stroke="#D97706"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              fill="none"
            />
            <path d="M10 8 L22 8 L20 14 H10 Z" fill="#FEF3C7" />
          </g>
          <defs>
            <linearGradient id="cartBg1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color: #fffbeb" />
              <stop offset="100%" style="stop-color: #fef3c7" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <div class="floating-cart floating-cart-2">
        <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="20" cy="20" r="20" fill="white" />
          <circle cx="20" cy="20" r="18" fill="#FEF3C7" />
          <g transform="translate(10, 8)">
            <rect x="2" y="6" width="16" height="12" rx="2" fill="#F59E0B" />
            <path
              d="M6 6 V4 C6 2 8 0 10 0 C12 0 14 2 14 4 V6"
              stroke="#D97706"
              stroke-width="2"
              fill="none"
            />
            <circle cx="10" cy="12" r="2" fill="white" />
          </g>
        </svg>
      </div>

      <div class="floating-cart floating-cart-3">
        <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="18" cy="18" r="18" fill="white" />
          <circle cx="18" cy="18" r="16" fill="#FEF3C7" />
          <g transform="translate(8, 8)">
            <path d="M10 0 L12 6 L20 6 L18 0 Z" fill="#FBBF24" />
            <rect x="2" y="6" width="16" height="10" rx="2" fill="#F59E0B" />
            <rect x="6" y="16" width="2" height="4" fill="#D97706" />
            <rect x="12" y="16" width="2" height="4" fill="#D97706" />
          </g>
        </svg>
      </div>

      <div class="floating-cart floating-cart-4">
        <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="16" cy="16" r="16" fill="white" />
          <circle cx="16" cy="16" r="14" fill="#FFFBEB" />
          <g transform="translate(7, 7)">
            <path d="M9 0 L18 0 L16 6 L2 6 L0 0 Z" fill="#FBBF24" opacity="0.5" />
            <path d="M2 6 L4 14 L14 14 L16 6 Z" fill="#F59E0B" />
            <circle cx="5" cy="17" r="2" fill="#D97706" />
            <circle cx="13" cy="17" r="2" fill="#D97706" />
          </g>
        </svg>
      </div>

      <div class="floating-cart floating-cart-5">
        <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="14" cy="14" r="14" fill="white" />
          <circle cx="14" cy="14" r="12" fill="#FEF3C7" />
          <text x="14" y="18" text-anchor="middle" fill="#D97706" font-size="14" font-weight="bold">
            +
          </text>
        </svg>
      </div>

      <!-- Sparkle particles -->
      <div class="sparkle sparkle-1"></div>
      <div class="sparkle sparkle-2"></div>
      <div class="sparkle sparkle-3"></div>
      <div class="sparkle sparkle-4"></div>
      <div class="sparkle sparkle-5"></div>
      <div class="sparkle sparkle-6"></div>
    </div>

    <div class="visual-card visual-card-1">
      <div class="card-icon">
        <svg
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <rect x="1" y="3" width="15" height="13" />
          <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
          <circle cx="5.5" cy="18.5" r="2.5" />
          <circle cx="18.5" cy="18.5" r="2.5" />
        </svg>
      </div>
      <span class="card-label">משלוח מהיר</span>
      <span class="card-value">24-48 שעות</span>
    </div>
    <div class="visual-card visual-card-2">
      <div class="card-icon">
        <svg
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
          <line x1="7" y1="7" x2="7.01" y2="7" />
        </svg>
      </div>
      <span class="card-label">מוצרים</span>
      <span class="card-value">1,000+</span>
    </div>
    <div class="visual-card visual-card-3">
      <div class="card-icon">
        <svg
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M12 22s-8-4.5-8-11.8A8 8 0 0112 2a8 8 0 018 8.2c0 7.3-8 11.8-8 11.8z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      </div>
      <span class="card-label">כיסוי</span>
      <span class="card-value">כל הארץ</span>
    </div>
  </div>

  <!-- Business Banner - Desktop (floating under hero) -->
  <BusinessBanner variant="desktop" />
</template>

<style src="../styles/hero-shared.css" scoped></style>
<style src="../styles/hero-desktop.css" scoped></style>
